import { ARDOQ_DEFAULT_FONT_FAMILY } from '@ardoq/typography';
import { Font, Size } from '@ardoq/yfiles';
import { ArrowType, LineType } from '@ardoq/api-types';

export const NodeDefaults = {
  HEIGHT: 75,
  WIDTH: 400,
  MARGIN: 5,
  BORDER_RADIUS: 20,
  DECORATION_MARGINS: 3,
  DECORATION_STROKE_THICKNESS: 2,
  MINIMUM_COLOR_LIGHTNESS: 0.5,
  MAX_LABEL_LENGTH: 25,
  TRANSPARENCY: 0.5,
};

export const GroupNodeDefaults = {
  // Group node corners are Bezier curves, and not arcs, so the radius, or
  // rather the distance from the endpoints of the curve to the control point,
  // have to be slightly larger than the radius of an arc with approximately the
  // same shape.
  BORDER_RADIUS: NodeDefaults.BORDER_RADIUS + 4,
  TRANSPARENCY: 0.8,
};

export const EdgeDefaults = {
  LABEL_OFFSET: 10,
  SMOOTHING_LENGTH: 20,
  MINIMUM_LAYER_DISTANCE: 150,
};

export const FontDefaults = {
  FONT_FAMILY: ARDOQ_DEFAULT_FONT_FAMILY,
  FONT_SIZE: 20,
  FONT_WIDTH: 10,
  FONT_WEIGHT: 300,
  LINE_HEIGHT: 30,
};

export const CollapseExpandButtonDefaults = {
  HEIGHT: 30,
  WIDTH: 30,
};

export const IconDefaults = {
  HEIGHT: 30,
  WIDTH: 30,
};

export const HORIZONTAL_ICON_MARGIN =
  NodeDefaults.BORDER_RADIUS - IconDefaults.WIDTH / 2;

export const VERTICAL_ICON_MARGIN =
  NodeDefaults.BORDER_RADIUS - IconDefaults.HEIGHT / 2;

export const NODE_FONT = new Font({
  fontFamily: FontDefaults.FONT_FAMILY,
  fontWeight: '300',
  fontSize: FontDefaults.FONT_SIZE,
});

export const NODE_LABEL_SIZE = new Size(
  NodeDefaults.WIDTH - (NodeDefaults.MARGIN * 2 + IconDefaults.WIDTH * 2),
  NodeDefaults.HEIGHT + NodeDefaults.MARGIN * 2
);

/**
 * In yFiles an arrow head placed on the opposite side of an arrow will point
 * the opposite way. Most places in ardoq-front they don't do that, so there are
 * separate start arrow types. In order for the start arrow heads to be rendered
 * the correct way here we need to map the start arrow heads to their
 * corresponding end arrow type.
 */
export const arrowMap = new Map<ArrowType, ArrowType | undefined>([
  [ArrowType.NONE, ArrowType.NONE],
  [ArrowType.BOTH, ArrowType.BOTH],
  [ArrowType.BOTH_START, ArrowType.BOTH],
  [ArrowType.DIAMOND, ArrowType.DIAMOND],
  [ArrowType.DIAMOND_START, ArrowType.DIAMOND],
  [ArrowType.DIAMOND_FILLED, ArrowType.DIAMOND_FILLED],
  [ArrowType.DIAMOND_FILLED_START, ArrowType.DIAMOND_FILLED],
  [ArrowType.TOP, ArrowType.TOP],
  [ArrowType.TOP_START, ArrowType.TOP],
  [ArrowType.BOTTOM, ArrowType.BOTTOM],
  [ArrowType.BOTTOM_START, ArrowType.BOTTOM],
  [ArrowType.BOTH_FILLED, ArrowType.BOTH_FILLED],
  [ArrowType.BOTH_FILLED_START, ArrowType.BOTH_FILLED],
  [ArrowType.BOTH_OUTLINED, ArrowType.BOTH_OUTLINED],
  [ArrowType.BOTH_OUTLINED_START, ArrowType.BOTH_OUTLINED],
  [ArrowType.CIRCLE, ArrowType.CIRCLE],
  [ArrowType.CIRCLE_START, ArrowType.CIRCLE],
  [ArrowType.ONE, ArrowType.ONE],
  [ArrowType.ONE_START, ArrowType.ONE],
  [ArrowType.ONE_MANDATORY, ArrowType.ONE_MANDATORY],
  [ArrowType.ONE_MANDATORY_START, ArrowType.ONE_MANDATORY],
  [ArrowType.MANY, ArrowType.MANY],
  [ArrowType.MANY_START, ArrowType.MANY],
  [ArrowType.ONE_OR_MANY, ArrowType.ONE_OR_MANY],
  [ArrowType.ONE_OR_MANY_START, ArrowType.ONE_OR_MANY],
  [ArrowType.ZERO_OR_ONE, ArrowType.ZERO_OR_ONE],
  [ArrowType.ZERO_OR_ONE_START, ArrowType.ZERO_OR_ONE],
  [ArrowType.ZERO_OR_MANY, ArrowType.ZERO_OR_MANY],
  [ArrowType.ZERO_OR_MANY_START, ArrowType.ZERO_OR_MANY],
  [ArrowType.DEFAULT, ArrowType.DEFAULT],
  [ArrowType.DEFAULT_START, ArrowType.DEFAULT],
]);

/**
 * In yFiles, if the line is not solid it is defined as a chain of smaller line
 * segments with one number representing the length of each line segment and one
 * representing the space between each line segment.
 */
export const lineTypeMap = {
  [LineType.SOLID]: 'solid',
  [LineType.DOTTED]: '1 2',
  [LineType.DASHED]: '4 2',
};

export const EDGE_LABEL_CONTAINER_CLASS_NAME = 'edge-label-group';
